// src/pages/SuperAdminDashboard.js
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    collection,
    query,
    where,
    onSnapshot,
    updateDoc,
    doc,
    getDocs,
} from "firebase/firestore";
import { db } from "../services/firebase";

const SuperAdminDashboard = () => {
    const [user, setUser] = useState(null);

    // Sekolah
    const [pendingSchools, setPendingSchools] = useState([]);
    const [recentApprovedSchools, setRecentApprovedSchools] = useState([]);

    // Guru & murid
    const [pendingTeachers, setPendingTeachers] = useState([]);
    const [stats, setStats] = useState({
        totalSchools: 0,
        approvedSchools: 0,
        pendingSchools: 0,
        totalTeachers: 0,
        pendingTeachers: 0,
        totalStudents: 0,
    });

    const navigate = useNavigate();

    // Protect route: hanya super admin boleh masuk
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (!stored) {
            navigate("/login");
            return;
        }

        const parsed = JSON.parse(stored);
        if (!parsed.isSuperAdmin) {
            navigate("/teacher/dashboard");
            return;
        }

        setUser(parsed);
    }, [navigate]);

    // Real-time: sekolah yang belum approved
    useEffect(() => {
        if (!user) return;

        const qPending = query(
            collection(db, "schools"),
            where("approved", "==", false)
        );

        const unsub = onSnapshot(qPending, (snap) => {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setPendingSchools(list);
        });

        return () => unsub();
    }, [user]);

    // Real-time: guru yang belum approved (di peringkat sistem)
    useEffect(() => {
        if (!user) return;

        const qPendingTeachers = query(
            collection(db, "users"),
            where("role", "==", "teacher"),
            where("approved", "==", false)
        );

        const unsub = onSnapshot(qPendingTeachers, (snap) => {
            const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setPendingTeachers(list);
        });

        return () => unsub();
    }, [user]);

    // Snapshot sekali-sekala: kira statistik & ambil sekolah terbaru
    useEffect(() => {
        if (!user) return;

        const fetchStats = async () => {
            // Semua sekolah
            const schoolsSnap = await getDocs(collection(db, "schools"));
            const schools = schoolsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            const approved = schools.filter((s) => s.approved);
            const pending = schools.filter((s) => !s.approved);

            // Ambil 5 sekolah yang paling baru diluluskan
            const recentApproved = [...approved]
                .sort((a, b) => {
                    const ta = a.createdAt?.seconds || 0;
                    const tb = b.createdAt?.seconds || 0;
                    return tb - ta;
                })
                .slice(0, 5);

            setRecentApprovedSchools(recentApproved);

            // Semua guru
            const teachersSnap = await getDocs(
                query(collection(db, "users"), where("role", "==", "teacher"))
            );
            const teachers = teachersSnap.docs.map((d) => d.data());
            const pendingT = teachers.filter((t) => !t.approved);

            // Semua murid
            const studentsSnap = await getDocs(
                query(collection(db, "users"), where("role", "==", "student"))
            );

            setStats({
                totalSchools: schools.length,
                approvedSchools: approved.length,
                pendingSchools: pending.length,
                totalTeachers: teachers.length,
                pendingTeachers: pendingT.length,
                totalStudents: studentsSnap.size,
            });
        };

        fetchStats();
    }, [user]);

    const handleApproveSchool = async (schoolId) => {
        try {
            await updateDoc(doc(db, "schools", schoolId), {
                approved: true,
            });
            // optional: boleh guna toast kalau ada
            // toast.success("Sekolah berjaya diluluskan");
        } catch (err) {
            console.error("Gagal luluskan sekolah:", err);
            alert("Gagal luluskan sekolah.");
        }
    };

    const handleBackHome = () => {
        if (!user) return;

        if (user.role === "teacher") {
            navigate("/teacher/dashboard");
        } else if (user.role === "student") {
            navigate("/student/dashboard");
        } else {
            navigate("/login");
        }
    };

    if (!user) return null;

    return (
        <div className="page-container superadmin-page">
            <header className="superadmin-header">
                <div>
                    <h1>Admin Sistem Tasmik</h1>
                    <p>Selamat datang, {user.name}</p>
                </div>
                <div className="superadmin-header-meta">
                    <button
                        type="button"
                        className="superadmin-back-button"
                        onClick={handleBackHome}
                    >
                        Kembali ke Dashboard
                    </button>
                    <div>
                        Peranan: <strong>Super Admin</strong><br />
                        Emel: {user.email}
                    </div>
                </div>
            </header>

            {/* Ringkasan Statistik */}
            <section className="superadmin-stats">
                <div className="superadmin-stat-card">
                    <h3>Jumlah Sekolah</h3>
                    <p className="stat-main">{stats.totalSchools}</p>
                    <p className="stat-sub">
                        {stats.approvedSchools} diluluskan · {stats.pendingSchools} menunggu
                    </p>
                </div>
                <div className="superadmin-stat-card">
                    <h3>Guru</h3>
                    <p className="stat-main">{stats.totalTeachers}</p>
                    <p className="stat-sub">
                        {stats.pendingTeachers} permohonan guru belum diluluskan
                    </p>
                </div>
                <div className="superadmin-stat-card">
                    <h3>Murid</h3>
                    <p className="stat-main">{stats.totalStudents}</p>
                    <p className="stat-sub">Aktif dalam sistem Tasmik</p>
                </div>
            </section>

            {/* Dua panel utama */}
            <section className="superadmin-panels">
                {/* Panel kiri: Permohonan sekolah */}
                <div className="superadmin-panel">
                    <div className="panel-header">
                        <h2>Permohonan Sekolah Baru</h2>
                        <span className="panel-tag">
                            {pendingSchools.length} permohonan
                        </span>
                    </div>

                    {pendingSchools.length === 0 ? (
                        <p>Tiada permohonan sekolah buat masa ini.</p>
                    ) : (
                        <div className="table-responsive">
                            <table className="progress-table">
                                <thead>
                                    <tr>
                                        <th>Nama Sekolah</th>
                                        <th>Negeri</th>
                                        <th>Alamat</th>
                                        <th>Telefon</th>
                                        <th>Kod Sekolah</th>
                                        <th>Dicipta Oleh</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingSchools.map((sch) => (
                                        <tr key={sch.id}>
                                            <td>{sch.name}</td>
                                            <td>{sch.negeri}</td>
                                            <td>{sch.address}</td>
                                            <td>{sch.phone}</td>
                                            <td>
                                                <code>{sch.secretCode}</code>
                                            </td>
                                            <td>{sch.createdBy}</td>
                                            <td>
                                                <button
                                                    className="login-button"
                                                    style={{ padding: "4px 12px", fontSize: "0.8rem" }}
                                                    onClick={() => handleApproveSchool(sch.id)}
                                                >
                                                    Luluskan
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Panel kanan: Aktiviti terkini */}
                <div className="superadmin-panel">
                    <h2>Ringkasan Aktiviti</h2>

                    <div className="superadmin-subpanel">
                        <h3>Sekolah Terbaru Diluluskan</h3>
                        {recentApprovedSchools.length === 0 ? (
                            <p>Belum ada sekolah yang diluluskan.</p>
                        ) : (
                            <ul className="superadmin-list">
                                {recentApprovedSchools.map((sch) => (
                                    <li key={sch.id}>
                                        <div className="superadmin-list-main">{sch.name}</div>
                                        <div className="superadmin-list-sub">
                                            {sch.negeri} · Kod: {sch.secretCode}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="superadmin-subpanel">
                        <h3>Permohonan Guru Belum Diluluskan</h3>
                        {pendingTeachers.length === 0 ? (
                            <p>Tiada permohonan guru buat masa ini.</p>
                        ) : (
                            <ul className="superadmin-list">
                                {pendingTeachers.map((t) => (
                                    <li key={t.id}>
                                        <div className="superadmin-list-main">{t.name}</div>
                                        <div className="superadmin-list-sub">
                                            {t.email} · {t.schools?.[0]?.schoolId
                                                ? `Sekolah: ${t.schools[0].schoolId}`
                                                : "Belum dipadankan sekolah"}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        <p className="superadmin-note">
                            * Kelulusan guru dibuat di peringkat sekolah melalui halaman Guru
                            (Teacher Dashboard).
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SuperAdminDashboard;
